import React, {
  createContext, useCallback, useContext, useEffect, useState, useRef
} from 'react';
import { useSearchParams, useLocation as useReactRouterLocation } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'tw1.global.location';

const LocationContext = createContext(null);

function readPersisted() {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (_) {
    return '';
  }
}

function writePersisted(slug) {
  try {
    if (slug) localStorage.setItem(STORAGE_KEY, slug);
    else localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* ignore quota / disabled storage */
  }
}

export function LocationProvider({ children }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLoc = searchParams.get('loc');
  const location = useReactRouterLocation(); // Detect route changes

  // URL first, then LocalStorage, then fallback to 'all'
  const [slug, setSlugRaw] = useState(() => {
    if (urlLoc) return urlLoc;
    const persisted = readPersisted();
    if (persisted) return persisted;
    return 'all';
  });

  const initializedDefault = useRef(false);

  // Calculate default based on user ONLY once when user becomes available
  useEffect(() => {
    if (!user || initializedDefault.current) return;
    initializedDefault.current = true;

    if (user.role === 'single_location') {
      // First try to fetch the role config, otherwise fallback to assigned location
      api.get('/system-config/default_landing_scope').then(res => {
        const config = res.data.value;
        const roleScope = config[user.role] || 'own';
        const assignedLoc = user.locations?.[0] || 'all';
        const newSlug = roleScope === 'own' ? assignedLoc : 'all';
        if (slug !== newSlug) {
          setSlugRaw(newSlug);
          writePersisted(newSlug);
        }
      }).catch(() => {
        const assignedLoc = user.locations?.[0] || 'all';
        if (slug !== assignedLoc) {
          setSlugRaw(assignedLoc);
          writePersisted(assignedLoc);
        }
      });
    } else {
      // For owner/admin/multi, if they don't have a persisted valid preference, check config or default to 'all'
      const params = new URLSearchParams(window.location.search);
      if (!params.get('loc') && !readPersisted()) {
        api.get('/system-config/default_landing_scope').then(res => {
          const config = res.data.value;
          const roleScope = config[user.role] || 'all';
          const newSlug = roleScope === 'own' && user.locations?.[0] ? user.locations[0] : 'all';
          setSlugRaw(newSlug);
          writePersisted(newSlug);
        }).catch(() => {
          setSlugRaw('all');
          writePersisted('all');
        });
      }
    }
  }, [user, slug]);

  const setGlobalLocation = useCallback((next) => {
    const value = next || 'all';
    setSlugRaw(value);
    writePersisted(value);
    
    // Update URL immediately
    setSearchParams(params => {
      const nextParams = new URLSearchParams(params);
      nextParams.set('loc', value);
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Sync from URL to context if URL changes via external forces (deep link, back button)
  // Only depend on urlLoc to avoid circular updates with the navigation effect below
  useEffect(() => {
    if (urlLoc) {
      // Update state if different
      if (urlLoc !== slug) {
        setSlugRaw(urlLoc);
      }
      // Always sync localStorage with URL (even if state was already correct from initialization)
      const persisted = readPersisted();
      if (urlLoc !== persisted) {
        writePersisted(urlLoc);
      }
    }
  }, [urlLoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure URL retains the `loc` param on route navigation — but ONLY when the
  // destination did NOT specify its own `loc`. An explicit link param (urlLoc)
  // must take PRECEDENCE over the persisted scope (handled by the effect above);
  // never overwrite it here, or deep-links land on the stale persisted location.
  useEffect(() => {
    // Skip transient redirect-only routes (e.g. legacy /goedkeuringen → Uren
    // review preset). Appending `loc` there would hijack the redirect target.
    if (location.pathname === '/goedkeuringen') return;
    if (!urlLoc && slug) {
      setSearchParams(params => {
        const nextParams = new URLSearchParams(params);
        nextParams.set('loc', slug);
        return nextParams;
      }, { replace: true });
    }
  }, [location.pathname, slug, urlLoc, setSearchParams]);

  const value = { globalLocation: slug, setGlobalLocation };
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useGlobalLocation() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useGlobalLocation must be used within <LocationProvider>');
  return ctx;
}

export function useLocationUrlSync() {
  const { globalLocation, setGlobalLocation } = useGlobalLocation();
  const slug = globalLocation === 'all' ? '' : globalLocation;
  
  const setSlug = useCallback((val) => {
    setGlobalLocation(val || 'all');
  }, [setGlobalLocation]);
  
  return { slug, setSlug };
}
